/** サポートコンソールの UI 状態を localStorage に永続化する（名前空間 `nx.support.*`）。
 *
 * ペインの開閉・ソート・本文の折り畳みなど、**チケット単位ではなく利用者単位**で
 * 覚えておきたい表示状態をここに集約する。保存に失敗しても UI は既定値で動作する
 * （プライベートウィンドウ・ストレージ無効環境）。 */

const NS = "nx.support.";

export function readUiState<T>(key: string, fallback: T): T {
  try {
    const raw = window.localStorage.getItem(NS + key);
    if (raw == null) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

export function writeUiState(key: string, value: unknown): void {
  try {
    window.localStorage.setItem(NS + key, JSON.stringify(value));
  } catch {
    /* 保存できなくても表示は継続する */
  }
}

import { useCallback, useState } from "react";

/** localStorage に永続化される useState。 */
export function useUiState<T>(key: string, fallback: T): [T, (v: T | ((prev: T) => T)) => void] {
  const [value, setValue] = useState<T>(() => readUiState(key, fallback));
  const set = useCallback(
    (v: T | ((prev: T) => T)) => {
      setValue((prev) => {
        const next = typeof v === "function" ? (v as (p: T) => T)(prev) : v;
        writeUiState(key, next);
        return next;
      });
    },
    [key],
  );
  return [value, set];
}
